import { InlineValue, ContentLink } from "@robe/client";
import { Box, Typography, Stack } from "@xcorejs/ui";
import { RssButton } from "components/Buttons/RssButton";
import Richtext from "design-system/Richtext";
import { ProductCardProductOrAccessory } from "design-system/robe";
import ProductCard from "design-system/robe/Cards/Product";
import LeftMenu from "design-system/robe/LeftMenuContainer/LeftMenu";
import Popover from "design-system/robe/Popover";
import { useState, useEffect } from "react";
import styled from "styled-components";
import { useLayout } from "xcore";
import { News, NewsDetailPage } from "xcore/types";
import NewsStack from "./NewsStack";

interface NewsArticleProps {
  news: News;
  newsDetailPage: NewsDetailPage;
  pdf?: boolean;
  locales?: string[];
}

export const NewsArticle = ({ news: { id, publishedAt, values }, newsDetailPage, pdf }: NewsArticleProps) => {
  const { stringify, formatDate, localize } = useLayout();
  const [isRssCopied, setIsRssCopied] = useState<boolean>(false);

  const publishedAtResolved = localize(publishedAt);
  const publishedAtFormatted = publishedAtResolved ? formatDate(publishedAtResolved)?.replace(/\//g, "&nbsp;/&nbsp;") : null;

  const htmlResolved = localize(values?.html);
  const productsFiltered = values.products?.filter(
    (p) => !!p.title && !!p.type && !!p.slug && !!p.values
  ) as ProductCardProductOrAccessory[];

  const buzzsproutAccountId = process.env.BUZZSPROUT_ACCOUNT_ID ?? "2411650";

  // Script has to run like this because React does not support automatic script executions in the JSX
  useEffect(() => {
    const buzzsproutEpisodeId = values.buzzsproutEpisodeId;
    if (buzzsproutEpisodeId) {
      const script = document.createElement("script");
      script.src = `https://www.buzzsprout.com/${buzzsproutAccountId}/episodes/${buzzsproutEpisodeId}.js?container_id=buzzsprout-player-${buzzsproutEpisodeId}&player=small`;
      script.type = "text/javascript";
      script.charset = "utf-8";
      script.async = true;

      document.body.appendChild(script);

      // Remove the script when the component unmounts
      return () => {
        document.body.removeChild(script);
      };
    }
  }, [buzzsproutAccountId, values.buzzsproutEpisodeId]);

  return (
    <>
      <Box width="85rem" maxWidth="100%" margin="auto">
        <Box display="flex" height="4rem" alignItems="center">
          {publishedAtFormatted && (
            <Typography
              width="100%"
              fontSize="1.2rem"
              lineHeight="1.6rem"
              color="#939598"
              textAlign="center"
              dangerouslySetInnerHTML={{ __html: publishedAtFormatted }}
            />
          )}
          {!pdf && (
            <Box position="relative" display="flex" alignItems="center">
              <RssButton position="absolute" right={0} isRssCopied={isRssCopied} setIsRssCopied={setIsRssCopied} />
            </Box>
          )}
        </Box>
        <Typography variant="h1" textAlign="center" mt=".5rem">
          <InlineValue value={stringify(values.title)} />
        </Typography>

        {productsFiltered && (
          <Box width="100%" mt="3rem">
            <Typography variant="h3" as="h2" mb={{ _: ".5rem", md: "1.5rem" }} textAlign={{ _: "center" }}>
              {stringify(newsDetailPage.values.productInvolved)}
            </Typography>
            <LeftMenu>
              <Stack gap="1.5rem" direction="row" justifyContent="center" flexWrap="wrap" {...({ flexFlow: "wrap" } as any)}>
                {productsFiltered.map((p) => (
                  <Popover
                    key={p.id}
                    popover={
                      <ContentLink content={p} noUILink>
                        <ProductCard product={p} width="30rem" cursor="pointer" />
                      </ContentLink>
                    }
                    _popover={{
                      marginLeft: { _: "0", md: "50%" },
                      left: { _: "50%", md: "auto" },
                      borderTop: "0.6rem solid transparent",
                    }}
                  >
                    <ContentLink content={p} queries={{ backto: id }} mt=".5rem">
                      <span style={{ color: "#e00034" }}>{stringify(p.title)}</span>
                    </ContentLink>
                  </Popover>
                ))}
              </Stack>
            </LeftMenu>
          </Box>
        )}

        {values.buzzsproutEpisodeId && (
          <PlayerWrapper
            id={`buzzsprout-player-${values.buzzsproutEpisodeId}`}
            width={{ _: "100%", sm: "80%" }}
            margin="0 auto"
            my="3rem"
          ></PlayerWrapper>
        )}

        {stringify(values.intro) && (
          <Typography variant="lead" textAlign="center" mt={values.buzzsproutEpisodeId ? 0 : "4rem"} mb="3rem">
            {stringify(values.intro)}
          </Typography>
        )}

        {htmlResolved && (
          <Box mb={{ _: "3rem" }}>
            <div dangerouslySetInnerHTML={{ __html: htmlResolved }}></div>
          </Box>
        )}
      </Box>

      <Box width="85rem" maxWidth="100%" margin="auto">
        <NewsStack>
          <Richtext value={values.content} _strong={{ color: "darkGray" }} />
        </NewsStack>
      </Box>
    </>
  );
};

export const newsPdfUrl = (id: number, locale: string) => `/api/robe/pdfs/${locale}/news/${id}`;

const PlayerWrapper = styled(Box)`
  /* target the injected iframe and zoom it out slightly, otherwise right side of the elements gets cut off */
  & iframe {
    transform: scale(0.95);
  }
`;
